import { message } from 'antd';
import { ArgsProps } from 'antd/es/message';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import md5 from 'js-md5';

import GlobalLoading from '@/base/GlobalLoading';
import { IS_SERVER } from '@/constant/index';
import { PLATFORM, FETCH_SECRET_KEY } from '@/constant/secret';
import { userStore, useSessionStore, UtilsStore } from '@/store';
import { getStorageUser } from '@/utils/storage';
import getDeviceId from '@/utils/deviceFingerprint';

// Initialize the agent at application startup.

const messageRef = IS_SERVER ? (null as any) : message;
export interface RequestHeaders {
  traceid: string;
  noncestr: string;
  platform: string;
  token?: string;
  timestamp?: number;
  version: string;
  sign?: string;
  did?: string;
}

/**
 * 将对象的非空基本类型字段按 key 排序后拼接为 key=value& 格式字符串。
 * 用于签名计算，排序保证相同数据生成相同签名字符串。
 */
export const covtObjToStr = (obj: Record<string, any>): string => {
  let param = '';
  if (obj) {
    const keys = Object.keys(obj).sort();
    for (const k of keys) {
      if (
        obj[k] !== null &&
        obj[k] !== undefined &&
        typeof obj[k] !== 'object'
      ) {
        param += `&${k}=${obj[k]}`;
      }
    }
  }

  return param.slice(1);
};

/**
 * 根据请求配置生成鉴权请求头。
 * sign 由请求体 + 请求头字段 + 密钥三部分拼接后 MD5 计算，防止请求篡改。
 */
export const getHeaders = async (config: AxiosRequestConfig): Promise<RequestHeaders> => {
  const data = config.data || config.params;
  const noncestr = Math.random().toString().slice(2, 10);
  // @ts-ignore-next-line
  const traceid = md5(
    new Date().getTime().toString() + Math.random().toString()
  );
  const userInfo: any = getStorageUser() || {};
  const { token = userInfo?.token, platform = PLATFORM } =
    (config.headers as any) || {};
  const header: RequestHeaders = {
    noncestr,
    platform,
    timestamp: Date.now(),
    traceid,
    version: '1.0.0', // 发布前需手动修改+++++++++++++++
    did: getDeviceId(),
  };
  header.token = token || '';

  // @ts-ignore-next-line
  header.sign = md5(
    covtObjToStr(data) + covtObjToStr(header) + FETCH_SECRET_KEY
  );

  return header;
};

let loadingCount = 0;
let loadingTimer: number | null = null;

/**
 * 为 Axios 实例注入 loading 拦截器。
 * 通过计数器支持并发请求（多个请求同时进行时不会提前关闭 loading），
 * 关闭时延迟 100ms 避免请求切换时 loading 闪烁。
 */
function loadingInterceptors(instance: AxiosInstance): void {
  const openLoading = (
    config: InternalAxiosRequestConfig<any>
  ): InternalAxiosRequestConfig<any> => {
    GlobalLoading.open();
    loadingCount++;
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    return config;
  };
  const closeLoading = () => {
    loadingCount--;
    if (loadingCount < 0) {
      loadingCount = 0;
    }

    if (loadingCount === 0) {
      loadingTimer = window.setTimeout(() => {
        GlobalLoading.close();
      }, 100);
    }
  };
  instance.interceptors.request.use(openLoading);
  instance.interceptors.response.use(
    response => {
      closeLoading();
      return response;
    },
    e => {
      closeLoading();
      throw e;
    }
  );
}

/**
 * 为 Axios 实例注入响应拦截器，统一处理以下场景：
 * - code 200：按 returnData 配置决定是否自动解包 data 字段
 * - content-type application/vnd.*：文件下载，透传 file + filename
 * - Blob 响应：直接透传
 * - code 40003：token 失效，清除用户信息并触发重新登录
 * - code 60003：无管理员权限，跳转 AI 页并刷新
 * - 其他错误：弹出 message 提示（code 20012 静默处理）
 */
function responseInterceptors(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    response => {
      if (response.data.code === 200) {
        if (
          (response.config as unknown as Record<string, unknown>).returnData
        ) {
          return response.data.data;
        }
        return response.data;
      } else if (
        response.headers['content-type'].includes('application/vnd.')
      ) {
        return {
          file: response.data,
          filename: decodeURIComponent(response.headers.filename),
        };
      } else if (response.data instanceof Blob) {
        return response.data;
      } else if (
        (response.config as unknown as Record<string, unknown>).noMessage
      ) {
        if (response.data instanceof ArrayBuffer) {
          const reader = new FileReader();
          const blob = new Blob([response.data]);
          reader.onload = () => {
            const { result } = reader;
            const { message: errMsg } = JSON.parse(result as string);
            messageRef?.destroy();
            messageRef?.error(errMsg);
          };
          reader.readAsText(blob);
          return Promise.reject(response);
        }
        const msgConfig: ArgsProps = {
          content: response.data.message,
          type: 'error',
          duration: 2,
        };

        if (response.data.code === 40003) {
          const path = window.location.href;
          const updateExpiredPath = UtilsStore.getState().updateExpiredPath;
          const setTriggerLogin = userStore.getState().setTriggerLogin;
          const removeUserInfo = userStore.getState().removeUserInfo;
          updateExpiredPath(path);
          setTriggerLogin();
          removeUserInfo();
        }

        if (response.data.code === 60003) {
          msgConfig.onClose = () => {
            useSessionStore.getState().changeBot({} as any);
            location.replace('#/ai');
            location.reload();
          };
        }

        if (![40003, 20012].includes(response.data.code)) {
          messageRef?.destroy();
          messageRef?.open(msgConfig);
        }

        /**
         *  不能把异常吃掉， 不然会走下去
         */
      }
      return Promise.reject(response.data);
    },
    error => {
      messageRef?.destroy();
      messageRef?.open({
        content:
          error?.message == 'timeout of 60000ms exceeded'
            ? '请求超时，请稍后重试…'
            : '正在加载中，请稍后…',
        duration: 2,
      });
      throw error;
    }
  );
}

/**
 * 创建带鉴权和响应处理的 Axios 实例。
 * @param useGlobalLoading 为 true 时附加全局遮罩拦截器（并发安全）
 */
export default function createAxiosInstance(
  baseURL = '',
  useGlobalLoading = false
): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 60000,
  });

  instance.interceptors.request.use(async config => {
    config.headers = Object.assign(await getHeaders(config), config.headers);

    return config;
  });

  useGlobalLoading && loadingInterceptors(instance);

  responseInterceptors(instance);
  return instance;
}
